import { useAppSelector } from '@/hooks';
import { useEffect, useState } from 'react';
import { SubmitHandler, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FormBaseFilter from '@/components/base-filter';

import useFetch from '@/hooks/useFetch';
import useOptionsGlobal from '@/hooks/useOptionsGlobal';

import { API } from '@/utils/constants/Apis';
import { DataViewer } from '@/utils/helpers/common';
import { formatDateTime } from '@/utils/helpers/globalHelper';
import { ICategory } from '@/utils/interfaces/masterdata';

import PrimaryFormFilter from './PrimaryFormFilter';
import { DEFAULT_FILTER_FORM } from './constant';
import { IFormFilter } from './models';

export interface ITableHeaderProps {
  onSubmit: SubmitHandler<IFormFilter>;
}
const TableHeader = ({ onSubmit }: ITableHeaderProps) => {
  const formFilter = useFormContext<IFormFilter>();
  const { t } = useTranslation();
  const { countryOptions } = useOptionsGlobal();
  const countries = useAppSelector((state) => state?.global?.countries);
  const { data: assignmentFormResponse } = useFetch<any>('/mst/forms', 'GET');
  const processPatterns = useAppSelector((state) => state.global.processPatterns);
  const [categoryCache, setCategoryCache] = useState<Map<string, ICategory[]>>(new Map());

  const selectedCountryId = formFilter.watch('primary.countryId');

  const { data: freshCategoryData } = useFetch<ICategory[] | null>(
    selectedCountryId ? API.GET_CATEGORY(selectedCountryId, 'TEMPLATE', '') : '',
    'GET'
  );

  useEffect(() => {
    if (freshCategoryData && selectedCountryId) {
      setCategoryCache((prev) => new Map(prev).set(selectedCountryId, freshCategoryData));
    }
  }, [freshCategoryData, selectedCountryId]);

  const getVisaCategories = () => {
    let categories: any[] = [];
    countries.forEach((item) => {
      categories = categories.concat(item.categories);
    });
    return categories;
  };

  const getCategoryName = (categoryId: string) => {
    if (selectedCountryId && categoryCache.has(selectedCountryId)) {
      const cached = categoryCache.get(selectedCountryId);
      const found = cached?.find((c) => c.id === categoryId);
      if (found) return found.name;
    }

    const visaCategories = getVisaCategories();
    return visaCategories?.find((i) => i.id === categoryId)?.name;
  };

  return (
    <div className='mb-4'>
      <FormBaseFilter
        onSubmit={onSubmit as any}
        searchBox={{ placeholder: DataViewer.display(t('common:TEMPLATE_FIELDS_SEARCH_PLACEHOLDER')) }}
        tagSection={{
          onClear: (field, meta) => {
            if (!field) {
              formFilter.setValue('primary', DEFAULT_FILTER_FORM.primary);
              formFilter.handleSubmit(onSubmit)();
              return;
            }

            if (meta?.key === 'countryId') {
              formFilter.setValue('primary', {
                ...formFilter.getValues('primary'),
                countryId: null,
                categoryId: null
              });
              formFilter.handleSubmit(onSubmit)();
              return;
            }

            formFilter.setValue('primary', {
              ...formFilter.getValues('primary'),
              ...field
            });
            formFilter.handleSubmit(onSubmit)();
          },
          renderTag: (fieldKey, fieldValue) => {
            if (fieldKey === 'countryId') {
              return <>{countryOptions?.find((i) => i.id === fieldValue)?.label}</>;
            }
            if (fieldKey === 'categoryId') {
              const categoryName = getCategoryName(fieldValue);
              return <>{categoryName || fieldValue}</>;
            }
            if (fieldKey === 'formId') {
              return <>{assignmentFormResponse?.data?.find((i: any) => i.id === fieldValue)?.name}</>;
            }
            if (fieldKey === 'patternId') {
              return <>{processPatterns?.find((i: any) => i.id === fieldValue)?.name}</>;
            }
            return <>{fieldValue}</>;
          }
        }}
        primaryAction={{
          label: 'button:filter',
          name: 'primary',
          popoverContent: (
            <PrimaryFormFilter
              defaultValues={formFilter.watch('primary')}
              onSubmit={(values) => {
                const previous = formFilter.getValues();
                formFilter.reset({
                  ...previous,
                  primary: {
                    ...values,
                    updatedDateFrom: values?.updatedDateFrom ? formatDateTime(values?.updatedDateFrom) : null,
                    updatedDateTo: values?.updatedDateTo ? formatDateTime(values?.updatedDateTo) : null
                  }
                });
                formFilter.handleSubmit(onSubmit)();
              }}
              onReset={() => {}}
            />
          )
        }}
        secondaryAction={null}
        thirdAction={null}
      />
    </div>
  );
};

export default TableHeader;
